import React, { useState, useEffect } from "react";
import { HeartPulse, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

// Iter76 — Redesigned "Profil médical d'urgence" component.
//
// Compact dark accordion that lives INSIDE alert cards (admin + agency).
// The previous rose/pink card was too tall and clashed with the dark
// admin dashboard chrome. This version:
//   • Collapsed by default (only a slim header row).
//   • Auto-expands when the alert is `Medical` (Urgence médicale).
//   • Dark bg matches the admin card (`bg-[#0f1318]`), zinc-800 border.
//   • Red is used SPARINGLY: the heart icon, blood-type value, and the
//     bold "Allergies" label — everything else stays neutral zinc.
//   • Compact 2-column grid for Nom / Âge / Groupe / Allergies on
//     mobile, 4-across on wider screens.
//   • Empty state renders a single 32 px row: "♡ Profil médical
//     d'urgence · Aucun profil renseigné." No large empty box.
//   • On first expand, POSTs to a role-specific audit endpoint so the
//     access is recorded (deduplicated 30 min server-side).

const CONDITION_LABELS = {
  diabetes: "Diabète",
  hypertension: "Hypertension",
  heart: "Problèmes cardiaques",
  asthma: "Asthme",
  seizures: "Épilepsie / crises",
  stroke_history: "Antécédent d'AVC",
  sickle_cell: "Drépanocytose",
  severe_allergy: "Allergie sévère",
  pregnancy: "Grossesse",
  disability: "Handicap / mobilité réduite",
};

function isEmpty(p) {
  if (!p || typeof p !== "object") return true;
  const keys = ["full_name","date_of_birth","age","blood_type","allergies","medications","disability","preferred_language","notes","other_condition"];
  return (
    !keys.some((k) => p[k]) &&
    !(Array.isArray(p.conditions) && p.conditions.length > 0) &&
    !p.pregnancy
  );
}

/**
 * @param profile     the medical_profile_snapshot dict (may be null)
 * @param alertId     the enclosing alert.id — required for audit ping
 * @param emergencyType String e.g. "Medical" — auto-expands when Medical
 * @param actor       "admin" | "agency" — routes the audit ping
 * @param testid      optional testid override (default "medical-profile-view")
 */
export function MedicalProfileView({
  profile,
  alertId,
  emergencyType,
  actor = "admin",
  testid = "medical-profile-view",
}) {
  const empty = isEmpty(profile);
  // Auto-expand only on Urgence médicale — this is the case where
  // responders NEED the info immediately.
  const [open, setOpen] = useState(emergencyType === "Medical" && !empty);
  const [pinged, setPinged] = useState(false);

  useEffect(() => {
    if (!open || pinged || !alertId) return;
    // Fire-and-forget audit ping. We don't await; the render must not
    // wait on the log write. Errors are swallowed intentionally —
    // failing to log must not prevent responders from seeing the data.
    const path = actor === "agency"
      ? `/agency/alerts/${alertId}/medical/access`
      : `/admin/alerts/${alertId}/medical/access`;
    api.post(path).catch(() => {});
    setPinged(true);
  }, [open, pinged, alertId, actor]);

  return (
    <section
      className="border border-zinc-800 bg-[#0f1318]"
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => !empty && setOpen((v) => !v)}
        disabled={empty}
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
        className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left ${empty ? "cursor-default" : "hover:bg-zinc-900/60"} transition-colors`}
      >
        <HeartPulse
          className={`w-3.5 h-3.5 shrink-0 ${empty ? "text-zinc-600" : "text-rose-400"}`}
          strokeWidth={2.2}
        />
        <span className="text-[11px] font-medium text-zinc-300">
          Profil médical d’urgence
        </span>
        {empty ? (
          <span className="ml-auto text-[10px] italic text-zinc-500" data-testid={`${testid}-empty`}>
            Aucun profil renseigné
          </span>
        ) : (
          <>
            {/* Concise inline preview when collapsed: blood type + allergy flag */}
            {!open && (
              <span className="ml-auto flex items-center gap-2 text-[10px]">
                {profile.blood_type && (
                  <span className="font-mono font-bold text-rose-300 tabular-nums">
                    {profile.blood_type}
                  </span>
                )}
                {profile.allergies && (
                  <span className="inline-flex items-center gap-0.5 text-amber-300" title="Allergies">
                    <AlertTriangle className="w-3 h-3" strokeWidth={2.5} />
                  </span>
                )}
              </span>
            )}
            {open ? (
              <ChevronUp className="w-3.5 h-3.5 text-zinc-500 shrink-0 ml-auto" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
            )}
          </>
        )}
      </button>

      {open && !empty && (
        <div
          className="px-2.5 pb-2 pt-1 border-t border-zinc-800/70 space-y-1.5"
          data-testid={`${testid}-body`}
        >
          {/* Primary 2×2 grid — the fields responders look at first. */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 sm:grid-cols-4">
            {(profile.full_name || profile.age) && (
              <>
                <Cell label="Nom" value={profile.full_name} />
                <Cell label="Âge" value={profile.age ? String(profile.age) : null} />
              </>
            )}
            <Cell
              label="Groupe sanguin"
              value={profile.blood_type}
              accent
            />
            <Cell
              label="Allergies"
              value={profile.allergies}
              accent={!!profile.allergies}
              danger={!!profile.allergies}
            />
          </div>

          {/* Secondary rows — only if something is there. */}
          {Array.isArray(profile.conditions) && profile.conditions.length > 0 && (
            <div>
              <div className="text-[10px] text-zinc-500 mb-0.5">Conditions médicales</div>
              <div className="flex flex-wrap gap-1">
                {profile.conditions.map((k) => (
                  <span
                    key={k}
                    className="text-[10px] leading-tight bg-zinc-800 text-zinc-200 px-1.5 py-0.5 rounded"
                  >
                    {CONDITION_LABELS[k] || k}
                  </span>
                ))}
                {profile.pregnancy && !profile.conditions.includes("pregnancy") && (
                  <span className="text-[10px] leading-tight bg-zinc-800 text-zinc-200 px-1.5 py-0.5 rounded">Grossesse</span>
                )}
              </div>
            </div>
          )}

          {profile.medications && (
            <Cell label="Traitements en cours" value={profile.medications} full />
          )}
          {profile.other_condition && (
            <Cell label="Autre condition" value={profile.other_condition} full />
          )}
          {profile.disability && (
            <Cell label="Handicap / mobilité" value={profile.disability} full />
          )}
          {profile.notes && (
            <Cell label="Notes médicales" value={profile.notes} full />
          )}
        </div>
      )}
    </section>
  );
}

function Cell({ label, value, full = false, accent = false, danger = false }) {
  if (!value) return null;
  return (
    <div className={full ? "col-span-2 sm:col-span-4" : ""}>
      <div className="text-[10px] text-zinc-500 leading-tight">{label}</div>
      <div
        className={`text-[12px] leading-tight break-words ${
          danger
            ? "text-rose-300 font-semibold"
            : accent
            ? "text-rose-300 font-mono font-bold tabular-nums"
            : "text-zinc-100"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
